import { useState, useEffect } from 'react'
import { supabase, initializeSupabase } from '@/lib/supabase'
import { useAuthStore } from '@/lib/store'

export function useSupabase() {
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const user = useAuthStore((state) => state.user)

  useEffect(() => {
    let mounted = true
    let retryCount = 0
    const maxRetries = 3

    const initialize = async () => {
      try {
        const success = await initializeSupabase()
        if (!success && retryCount < maxRetries) {
          // Retry with exponential backoff
          retryCount++
          const delay = 1000 * Math.pow(2, retryCount)
          console.log(`Retrying Supabase connection in ${delay}ms (attempt ${retryCount})`)
          setTimeout(initialize, delay)
          return
        }
        
        if (!success) {
          throw new Error('Failed to initialize Supabase after multiple attempts')
        }
        
        if (mounted) {
          setIsReady(true)
          setError(null)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'))
          setIsReady(false)
          
          // Retry with exponential backoff
          if (retryCount < maxRetries) {
            retryCount++
            const delay = 1000 * Math.pow(2, retryCount)
            console.log(`Retrying Supabase connection in ${delay}ms (attempt ${retryCount})`)
            setTimeout(initialize, delay)
          }
        }
      }
    }

    initialize()

    // Remove auth state change subscription to prevent loops
    return () => {
      mounted = false
    }
  }, [])

  return {
    supabase,
    isReady,
    error,
    user
  }
}